<template>
  <!-- Bottom Sheet Overlay -->
  <div 
    v-if="showBottomSheet"
    class="fixed inset-0 bg-[##0B0C0E] z-50 flex items-end justify-center"
    @click="closeBottomSheet"
    dir="ltr"
  >
    <!-- Bottom Sheet Content -->
    <div 
      class="bg-[#14151A] rounded-t-3xl w-full max-w-md mx-auto p-6 transform transition-transform duration-300 ease-out"
      :class="{ 'translate-y-0': showBottomSheet, 'translate-y-full': !showBottomSheet }"
      @click.stop
      dir=""
    >
      <!-- Close Button -->
      <button 
        @click="closeBottomSheet"
        class="absolute top-4 right-4 text-gray-400 hover:text-white"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Handle Bar -->
      <div class="w-12 h-1 bg-gray-600 rounded-full mx-auto mb-6"></div>

      <!-- Content -->
      <div class="text-center">
        <h2 class="text-xl font-bold text-white mb-2 font-Almarai">
          أدخل كوبون الخصم
        </h2>

        <!-- Coupon Input -->
        <div class="mb-4 text-right">
          <input
            v-model="couponCode"
            type="text"
            placeholder="أدخل كوبون الخصم"
            :class="[
              'w-full px-4 py-3 bg-transparent border rounded-xl text-white placeholder-gray-400 font-Almarai text-center transition-colors duration-200',
              isValidCoupon === null ? 'border-gray-600 focus:border-[#9d2890]' : 
              isValidCoupon === true ? 'border-green-500' :
              'border-red-500'
            ]"
            @input="resetValidation"
            @keypress.enter="validateCoupon"
          />
          
          <!-- Error Message -->
          <div 
            v-if="isValidCoupon === false" 
            class="flex items-center justify-center mt-2 text-red-500 text-sm font-Almarai"
          >
            <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            الكوبون غير صالح!
          </div>
        </div>

        <!-- Buttons -->
        <div class="space-y-3">
          <button
            @click="validateCoupon"
            :disabled="!couponCode.trim() || isLoading"
            :class="[
              'w-full font-semibold py-4 px-6 rounded-xl transition-colors duration-200 font-Almarai',
              couponCode.trim() && !isLoading 
                ? 'bg-[#9d2890] hover:bg-[#8a2279] text-white' 
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            ]"
          >
            <span v-if="!isLoading">تطبيق الخصم</span>
            <span v-else class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              جاري التحقق...
            </span>
          </button>
          
          <button
            @click="closeBottomSheet"
            class="w-full border border-gray-600 hover:border-gray-500 text-gray-200 hover:text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-200 font-Almarai"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AUTHTOKEN, ISACTIVE, USER } from '@/constants';
import { useAuthStore } from '@/stores/auth';
import { mapActions } from 'pinia';


export default {
  name: "CouponRedemption",
  data() {
    return {
      showBottomSheet: false,
      couponCode: '',
      isValidCoupon: null, // null = not validated, true = valid, false = invalid
      isLoading: false,
    };
  },
  methods: {
    ...mapActions(useAuthStore  , ['redeemCoupon']),
    openBottomSheet() {
      this.showBottomSheet = true;
    },
    closeBottomSheet() {
      this.showBottomSheet = false;
      this.resetForm();
      this.$router.go(-1);
    },
    resetForm() {
      this.couponCode = '';
      this.isValidCoupon = null;
      this.isLoading = false;
    },
    resetValidation() {
      this.isValidCoupon = null;
    },
    async validateCoupon() {
      if (!this.couponCode.trim()) {
        console.warn('Cannot validate empty coupon code');
        return;
      }
      
      console.log('Validating coupon code:', this.couponCode);
      this.isLoading = true;
      
      try {
        // Call the real API endpoint
        const response = await this.redeemCoupon(this.couponCode.trim());
        
        console.log('Coupon validation response:', response);
        
        if (response.data.success) {
          console.log('Coupon is valid, updating state');
          this.isValidCoupon = true;
          console.log('Coupon is valid, navigating to success page in 1 second');
          // Wait a moment to show success state
          if (response.data.user) {
            window.localStorage.setItem(USER, JSON.stringify(response.data.user));
            window.localStorage.setItem(AUTHTOKEN, response.data.token);
            window.localStorage.setItem(ISACTIVE, response.data.token ? true : false);
          }
          setTimeout(() => {
            this.showSuccessPage();
          }, 1000);
        } else {
          console.log(response.data.message);
          console.log('Coupon is invalid');
          if(response.data.message=="User has an active subscription. Cannot apply coupon."){
            this.$toast.info("لديك اشتراك نشط. لا يمكن تطبيق الكوبون.");
          }
          this.isValidCoupon = false;
        }
      } catch (error) {
        console.error('Coupon validation error:', error);
        this.isValidCoupon = false;
      } finally {
        this.isLoading = false;
      }
    },
    
    // Mock API call - replace with actual implementation
    async checkCouponAPI(couponCode) {
      return new Promise((resolve) => {
        setTimeout(() => {
          // Mock validation logic
          const validCoupons = ['Nasna2025', 'DISCOUNT20', 'WELCOME'];
          const isValid = validCoupons.includes(couponCode);
          resolve({ data: { success: isValid, message: isValid ? "Coupon is valid" : "Coupon is invalid" , 
          user: { id: 1, name: "Test User" }, token: "abcd1234" }});
        }, 1500);
      });
    },
    
    showSuccessPage() {
      console.log('Navigating to success page with coupon code:', this.couponCode);
      
      // Ensure couponCode is not empty
      if (!this.couponCode || !this.couponCode.trim()) {
        console.error('Cannot navigate to success page: couponCode is empty');
        return;
      }
      
      // Store coupon code before closing bottom sheet
      const couponCodeToPass = this.couponCode.trim();
      console.log('Storing coupon code for navigation:', couponCodeToPass);
      
      // Close bottom sheet without resetting form data
      this.showBottomSheet = false;
      
      // Force navigation to success page with explicit route
      console.log('Forcing navigation to coupon success page...');
      
      // Use router.replace to prevent back navigation issues
      this.$router.replace({ 
        name: 'coupon.success', 
        params: { couponCode: couponCodeToPass },
        query: { code: couponCodeToPass }
      }).catch(error => {
        console.error('Navigation error:', error);
        // Fallback to path-based navigation
        this.$router.replace({ 
          path: '/coupon/success/' + encodeURIComponent(couponCodeToPass),
          query: { code: couponCodeToPass }
        });
      });
    }
  },
  created() {
    // Show bottom sheet automatically
    setTimeout(() => {
      this.openBottomSheet();
    }, 500);
  }
};
</script>

<style scoped>
/* Bottom sheet animation */
.transform {
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* Input focus styles */
input:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(157, 40, 144, 0.2);
}

/* Button hover effects */
button:hover:not(:disabled) {
  transform: translateY(-1px);
}

button:active {
  transform: translateY(0);
}

/* Smooth transitions for all interactive elements */
button, input {
  transition: all 0.2s ease-in-out;
}

/* Loading spinner animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>